/*
 * GemKrishna product image magnifier
 *
 * Desktop product images use the magnifier only; mobile images can open
 * the clean product image popup.
 *
 * The magnifier runs only on desktop and follows the mouse smoothly.
 */

(() => {
    if (window.__gkMagnifierV4Installed) return;
    window.__gkMagnifierV4Installed = true;

    const MIN_WIDTH = 750;
    const DESKTOP_QUERY = window.matchMedia(
      `(min-width: ${MIN_WIDTH}px) and (hover: hover) and (pointer: fine)`
    );
    const REDUCED_MOTION_QUERY = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    const LENS_FALLBACK_SIZE = 152;
    const ZOOM = 2.7 * 0.75; // 2.025x: exactly 25% less than 2.7x.
    const EASE = 0.48;
    const EDGE_GAP = 8;
    const SETTLE_EPSILON = 0.1;

    let lens = null;
    let lensImage = null;
    let activeSurface = null;
    let activeImage = null;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;

    let targetLocalX = 0;
    let targetLocalY = 0;
    let currentLocalX = 0;
    let currentLocalY = 0;

    let raf = null;
    let visible = false;
    let lensHalf = LENS_FALLBACK_SIZE / 2;

    function desktop() {
      return DESKTOP_QUERY.matches;
    }

    function ensureLens() {
      if (lens && lens.isConnected) return;

      lens = document.createElement('div');
      lens.className = 'gk-v4-magnifier';
      lens.setAttribute('aria-hidden', 'true');

      lensImage = document.createElement('img');
      lensImage.className = 'gk-v4-magnifier__image';
      lensImage.alt = '';
      lensImage.setAttribute('aria-hidden', 'true');
      lensImage.decoding = 'async';
      lensImage.draggable = false;

      lens.appendChild(lensImage);
      document.body.appendChild(lens);

      lensHalf = (lens.offsetWidth || LENS_FALLBACK_SIZE) / 2;
    }

    function hide() {
      visible = false;
      activeSurface = null;
      activeImage = null;

      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }

      if (lens) {
        lens.classList.remove('is-visible');
      }
    }

    function startRendering() {
      if (!raf) {
        raf = requestAnimationFrame(render);
      }
    }

    function clamp(value, minimum, maximum) {
      return Math.min(
        Math.max(value, minimum),
        Math.max(minimum, maximum)
      );
    }

    function getMagnifySource(surface, image) {
      return (
        surface.dataset.gkMagnifySrc ||
        image.currentSrc ||
        image.src ||
        ''
      );
    }

    function getSurfaceFromPoint(x, y) {
      const node = document.elementFromPoint(x, y);
      if (!node) return null;

      return node.closest?.('[data-gk-image-magnify]') || null;
    }

    function activate(surface, event) {
      const image = surface.querySelector('.gk-product-main-image');
      if (!image) {
        hide();
        return;
      }

      const rect = surface.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        hide();
        return;
      }

      ensureLens();

      activeSurface = surface;
      activeImage = image;

      targetX = event.clientX;
      targetY = event.clientY;
      currentX = event.clientX;
      currentY = event.clientY;

      targetLocalX = event.clientX - rect.left;
      targetLocalY = event.clientY - rect.top;
      currentLocalX = targetLocalX;
      currentLocalY = targetLocalY;

      const highRes = getMagnifySource(surface, image);

      if (!highRes) {
        hide();
        return;
      }

      if (lensImage.dataset.src !== highRes) {
        lensImage.src = highRes;
        lensImage.dataset.src = highRes;
      }

      const computed = window.getComputedStyle(image);

      lensImage.style.width = `${rect.width}px`;
      lensImage.style.height = `${rect.height}px`;
      lensImage.style.objectFit = computed.objectFit || 'cover';
      lensImage.style.objectPosition =
        computed.objectPosition || '50% 50%';

      lensHalf = (lens.offsetWidth || LENS_FALLBACK_SIZE) / 2;

      visible = true;
      lens.classList.add('is-visible');
      startRendering();
    }

    function onMouseMove(event) {
      if (!desktop()) {
        hide();
        return;
      }

      const surface = getSurfaceFromPoint(
        event.clientX,
        event.clientY
      );

      if (!surface) {
        hide();
        return;
      }

      const rect = surface.getBoundingClientRect();

      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        hide();
        return;
      }

      if (
        surface !== activeSurface ||
        !activeImage ||
        !activeImage.isConnected ||
        !visible
      ) {
        activate(surface, event);
        return;
      }

      if (
        lensImage &&
        getMagnifySource(surface, activeImage) !==
          lensImage.dataset.src
      ) {
        activate(surface, event);
        return;
      }

      targetX = event.clientX;
      targetY = event.clientY;

      targetLocalX =
        event.clientX - rect.left;

      targetLocalY =
        event.clientY - rect.top;

      startRendering();
    }

    function render() {
      if (
        !desktop() ||
        !visible ||
        !activeSurface ||
        !activeImage ||
        !activeImage.isConnected ||
        !lens ||
        !lensImage
      ) {
        hide();
        return;
      }

      const rect =
        activeSurface.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        hide();
        return;
      }

      const ease = REDUCED_MOTION_QUERY.matches ? 1 : EASE;

      currentX +=
        (targetX - currentX) * ease;

      currentY +=
        (targetY - currentY) * ease;

      currentLocalX +=
        (targetLocalX - currentLocalX) * ease;

      currentLocalY +=
        (targetLocalY - currentLocalY) * ease;

      const remainingDistance = Math.max(
        Math.abs(targetX - currentX),
        Math.abs(targetY - currentY),
        Math.abs(targetLocalX - currentLocalX),
        Math.abs(targetLocalY - currentLocalY)
      );

      const settled = remainingDistance <= SETTLE_EPSILON;

      if (settled) {
        currentX = targetX;
        currentY = targetY;
        currentLocalX = targetLocalX;
        currentLocalY = targetLocalY;
      }

      const lensCenterX = clamp(
        currentX,
        lensHalf + EDGE_GAP,
        window.innerWidth - lensHalf - EDGE_GAP
      );

      const lensCenterY = clamp(
        currentY,
        lensHalf + EDGE_GAP,
        window.innerHeight - lensHalf - EDGE_GAP
      );

      lens.style.transform =
        `translate3d(${lensCenterX - lensHalf}px, ${lensCenterY - lensHalf}px, 0)`;

      const tx = lensHalf - currentLocalX * ZOOM;
      const ty = lensHalf - currentLocalY * ZOOM;

      lensImage.style.transform =
        `translate3d(${tx}px, ${ty}px, 0) scale(${ZOOM})`;

      raf = settled ? null : requestAnimationFrame(render);
    }

    document.addEventListener(
      'mousemove',
      onMouseMove,
      { passive: true }
    );

    document.documentElement.addEventListener(
      'mouseleave',
      hide,
      { passive: true }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) hide();
      }
    );

    window.addEventListener(
      'scroll',
      hide,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      hide,
      { passive: true }
    );

    window.addEventListener(
      'blur',
      hide
    );

    if (typeof DESKTOP_QUERY.addEventListener === 'function') {
      DESKTOP_QUERY.addEventListener('change', hide);
    } else if (typeof DESKTOP_QUERY.addListener === 'function') {
      DESKTOP_QUERY.addListener(hide);
    }
  })();

/* ==========================================================
   GEMKRISHNA CLEAN PRODUCT IMAGE POPUP
   - Desktop image popup disabled.
   - Mobile image popup enabled.
   ========================================================== */

const gkDesktopMediaQuery =
  window.matchMedia('(min-width: 750px)');

function syncGkImageModalButtons() {
  const isDesktop = gkDesktopMediaQuery.matches;

  document
    .querySelectorAll('[data-gk-image-modal-toggle]')
    .forEach((button) => {
      button.disabled = isDesktop;

      if (isDesktop) {
        button.setAttribute('aria-hidden', 'true');
        button.removeAttribute('aria-haspopup');
      } else {
        button.removeAttribute('aria-hidden');
        button.setAttribute('aria-haspopup', 'dialog');
      }
    });

  if (isDesktop) {
    document
      .querySelectorAll('product-modal[open]')
      .forEach((modal) => {
        if (typeof modal.hide === 'function') {
          modal.hide();
        }
      });
  }
}

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.gkClosing = false;
        this.gkCloseTimer = null;
      }

      resetMediaVisibility() {
        this.querySelectorAll(
          '.product-media-modal__content [data-media-id]'
        ).forEach((media) => {
          media.style.display = '';
          media.classList.remove('gk-modal-active-media');
        });
      }

      hide() {
        if (!this.hasAttribute('open')) {
          this.resetMediaVisibility();
          return;
        }

        if (this.gkClosing) return;

        this.gkClosing = true;
        this.classList.add('gk-modal-closing');

        window.clearTimeout(this.gkCloseTimer);

        this.gkCloseTimer = window.setTimeout(() => {
          super.hide();

          this.classList.remove('gk-modal-closing');
          this.classList.remove('gk-modal-opened');

          this.gkClosing = false;
          this.resetMediaVisibility();
        }, 180);
      }

      show(opener) {
        const isDesktop = gkDesktopMediaQuery.matches;
        const isImageOpener =
          opener?.closest?.('.product__modal-opener--image');

        /* Never open an image popup on desktop. */
        if (isDesktop && isImageOpener) return;

        this.classList.remove('gk-modal-closing');
        this.gkClosing = false;

        super.show(opener);

        const modalContent =
          this.querySelector('.product-media-modal__content');

        if (!modalContent) return;

        let clickedMediaId = null;

        if (opener) {
          clickedMediaId =
            opener.getAttribute('data-media-id') ||
            opener
              .querySelector('[data-media-id]')
              ?.getAttribute('data-media-id');
        }

        if (
          !clickedMediaId &&
          document.activeElement
        ) {
          clickedMediaId =
            document.activeElement.getAttribute('data-media-id') ||
            document.activeElement
              .closest('[data-media-id]')
              ?.getAttribute('data-media-id');
        }

        if (clickedMediaId) {
          const cleanMediaId =
            clickedMediaId.toString().split('-').pop();

          const allMedia =
            modalContent.querySelectorAll('[data-media-id]');

          allMedia.forEach((media) => {
            const modalMediaId =
              media.getAttribute('data-media-id');

            if (
              modalMediaId === clickedMediaId ||
              modalMediaId === cleanMediaId
            ) {
              media.style.display = 'block';
              media.classList.add('gk-modal-active-media');
            } else {
              media.style.display = 'none';
              media.classList.remove('gk-modal-active-media');
            }
          });
        }

        requestAnimationFrame(() => {
          this.classList.add('gk-modal-opened');

          window.setTimeout(() => {
            this.classList.remove('gk-modal-opened');
          }, 280);
        });
      }
    }
  );
}

/* Extra safety: desktop clicks on product images never open the modal. */
document.addEventListener(
  'click',
  (event) => {
    if (!gkDesktopMediaQuery.matches) return;

    const opener =
      event.target.closest?.('.product__modal-opener--image');

    if (!opener) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  },
  true
);

function initGkImagePopupState() {
  syncGkImageModalButtons();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initGkImagePopupState,
    { once: true }
  );
} else {
  initGkImagePopupState();
}

if (
  typeof gkDesktopMediaQuery.addEventListener === 'function'
) {
  gkDesktopMediaQuery.addEventListener(
    'change',
    syncGkImageModalButtons
  );
} else if (
  typeof gkDesktopMediaQuery.addListener === 'function'
) {
  gkDesktopMediaQuery.addListener(
    syncGkImageModalButtons
  );
}

document.addEventListener(
  'shopify:section:load',
  syncGkImageModalButtons
);
